/** @jsxImportSource @emotion/react */
import React, {FC, useState, ReactElement, ReactNode, useRef, useCallback, useLayoutEffect} from 'react';
import * as ReactDOM from 'react-dom';
import { css, SerializedStyles } from '@emotion/react';
import { WHITE } from '../styles/colors';
import { MediumText } from '../styles/styles';

interface Props {
  content: ReactElement;
  children: React.ReactElement | ReactNode[];
  delay?: number;
  width?: number;
  center? : boolean;
  backgroundColor?: string;
  disabled?: boolean;
  border?: string;
  offsetTop?: number;
  borderRadius?: string;
  /** Arbitrary style overrides for the tooltip content box; applied last so it wins over the other overrides. */
  contentCss?: SerializedStyles;
  /** When false, the tooltip stays open until the pointer/focus leaves (no auto-dismiss timer). Default true. */
  autoHide?: boolean;
  /**
   * When true, the content box is portaled to document.body and positioned with `position: fixed`, anchored below the
   * trigger. Use this when the tooltip lives inside a clipping ancestor (e.g. an `overflow: hidden` card) that would
   * otherwise cut off long content. Hover-to-scroll is preserved via a short grace period on hide.
   */
  escapeOverflow?: boolean;
}

const contentVisualStyle = css`
  z-index: 999;
  background-color: rgb(30 30 30 / 80%);
  color: ${WHITE};
  padding: 0.5rem;
  border-radius: 0.5rem;
  ${MediumText};
  text-wrap: wrap;
  overflow-wrap: break-word;
  text-align: left;
`;

const defaultContentStyle = css`
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  ${contentVisualStyle};
`;

/** As above, but fixed to the viewport so a clipping ancestor can't cut it off. Position comes from the trigger rect. */
const fixedContentStyle = css`
  position: fixed;
  transform: translateX(-50%);
  ${contentVisualStyle};
`;

const centeredContainer = css`
  left: 25%;
`;

/** Grace period (ms) before an escapeOverflow tooltip hides, so the pointer can cross the gap into the content. */
const HIDE_GRACE_MS = 120;

/** Vertical gap (px) between the trigger and a portaled tooltip. */
const FIXED_GAP_PX = 6;

/**
 * A simple tooltip component to replace the current Tooltip component that has bugs
 * @param content
 * @param children
 * @constructor
 */
export const TooltipFC: FC<Props> = ({ content, children, delay, width, center, backgroundColor, disabled, border, offsetTop, borderRadius, contentCss, autoHide = true, escapeOverflow = false }) => {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tooltipWidth = width ?? 200;
  const centerContainer = center ? `${centeredContainer}` : '';
  const overrideBackground = backgroundColor ? `background-color: ${backgroundColor}!important;` : '';
  const overrideBorder = border ? `border: ${border};` : '';
  const overrideTop = offsetTop !== undefined ? `top: ${offsetTop}px;` : '';
  const overrideBorderRadius = borderRadius !== undefined ? `border-radius: ${borderRadius};` : '';

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + FIXED_GAP_PX, left: rect.left + rect.width / 2 });
  }, []);

  // Keep the portaled tooltip anchored to the trigger while it is open (grid scroll, window resize, ...).
  useLayoutEffect(() => {
    if (!escapeOverflow || !show) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [escapeOverflow, show, updatePosition]);

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (escapeOverflow) {
      // Delay so moving the pointer from the trigger into the portaled content doesn't dismiss it mid-gap.
      timeoutRef.current = setTimeout(() => setShow(false), HIDE_GRACE_MS);
      return;
    }
    setShow(false);
  }

  const showTooltip = () => {
    if (escapeOverflow) {
      // Always clear a pending hide so re-entering the trigger or content keeps the tooltip open.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (!show) {
        setShow(true);
      }
      if (autoHide) {
        timeoutRef.current = setTimeout(() => setShow(false), delay ?? 5000);
      }
      return;
    }
    if (!show) {
      setShow(true)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (autoHide) {
        timeoutRef.current = setTimeout(() => {
          setShow(false);
        }, delay ?? 5000);
      }
    }
  }

  const contentBox = (
    <div
      onMouseEnter={escapeOverflow ? showTooltip : undefined}
      onMouseLeave={escapeOverflow ? hideTooltip : undefined}
      style={escapeOverflow ? { top: coords.top, left: coords.left } : undefined}
      css={css`${escapeOverflow ? fixedContentStyle : defaultContentStyle}; width:${tooltipWidth}px; visibility: ${show && !disabled ? "visible" : "hidden"}; ${overrideBackground}; ${overrideBorder}; ${escapeOverflow ? '' : overrideTop}; ${overrideBorderRadius}; ${contentCss}`}
    >
      {content}
    </div>
  );

  return (
    // Hover handlers live on the container (the DOM ancestor of both the content box and the trigger) so the
    // pointer can move from the trigger into the tooltip content without dismissing it — required for the content
    // to be interactive (e.g. scrolling a long list). mouseenter/leave treat an element and its descendants as one.
    // When escapeOverflow is set the content is portaled out, so that interaction is preserved by the content's own
    // hover handlers plus the hide grace period instead.
    <div css={css`${centerContainer}; position: relative; display: inline-block;`}
      onFocus={showTooltip}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onClick={showTooltip}
      onBlur={hideTooltip}>
      {escapeOverflow
        ? (show && !disabled ? ReactDOM.createPortal(contentBox, document.body) : null)
        : contentBox}
      <div ref={triggerRef} css={css`display: inline-block; cursor: pointer;`}>
        {children}
      </div>
    </div>
  );
};